import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import {
  deepLinkService,
  DeepLinkRoute,
  externalAppUrlToRouterPath,
  isNativeDeepLinkDebounced,
  isRedundantReviewDeepLink,
  markNativeDeepLinkHandled,
  markOpenedViaDeepLink,
  wasAlreadyAppliedThisSession,
} from '../services/deepLinkService';
import { setDeepLinkNavigator } from '../capacitor-init';

/** iOS Scene cold-start sets `lastURL` after JS may first call `getLaunchUrl`; Capacitor also double-resolves that API. Poll briefly. */
async function getIosLaunchUrlWithRetry(maxAttempts = 8, delayMs = 350): Promise<string | undefined> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const result = await App.getLaunchUrl();
      const url = result?.url;
      if (url) return url;
    } catch {
      // non-fatal
    }
    if (attempt < maxAttempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
  return undefined;
}

/**
 * Hook to handle deep link navigation
 * Native: registers navigate with capacitor-init (single appUrlOpen handler + cold-start URL).
 * Web: in-app listeners only if deepLinkService notifies (currently unused on web).
 */
export function useDeepLinkNavigation(): void {
  const navigate = useNavigate();

  useEffect(() => {
    deepLinkService.initialize();

    if (Capacitor.isNativePlatform()) {
      // Snapshot the entry path at hook-mount. Used to decide whether the user
      // has navigated away while we were polling for the cold-start launch URL
      // (or before iOS re-delivers a universal link via appUrlOpen).
      const initialPath = window.location.pathname;

      const userHasNavigatedAway = (targetPathOnly: string): boolean => {
        const currentPath = window.location.pathname;
        // Respect the user's choice unless they're still at the initial entry,
        // a tab root, or already on the target.
        return (
          currentPath !== initialPath &&
          currentPath !== '/' &&
          currentPath !== targetPathOnly
        );
      };

      const nav = (to: string) => {
        const toPathOnly = to.split('?')[0];
        // iOS sometimes re-delivers the same universal link (e.g. on resume).
        // If we've already applied this URL once AND the user has moved past
        // it, don't yank them back. Marking happens AFTER this check so a
        // genuine first-time foreground delivery (user already mid-app) is
        // honored.
        if (wasAlreadyAppliedThisSession(to) && userHasNavigatedAway(toPathOnly)) {
          console.log('🔗 Ignoring deep-link re-delivery while user is on:', window.location.pathname);
          return;
        }
        // Replace the initial history entry so back always returns to home, not the deep link URL.
        markNativeDeepLinkHandled(to);
        markOpenedViaDeepLink();
        navigate(to, { replace: true });
      };
      setDeepLinkNavigator(nav);

      const applyLaunchUrl = (url: string) => {
        const loc = externalAppUrlToRouterPath(url);
        if (!loc) return;
        if (isRedundantReviewDeepLink(loc)) {
          return;
        }
        if (isNativeDeepLinkDebounced(loc)) {
          return;
        }
        // Cold-start polling can resolve up to ~2.8s after mount. If the user
        // has already tapped a tab or opened something else in the meantime,
        // respect that — don't snap them back to the shared item.
        const locPathOnly = loc.split('?')[0];
        if (userHasNavigatedAway(locPathOnly)) {
          console.log('🔗 User already navigated; skipping cold-start launch URL:', loc);
          return;
        }
        // Already applied earlier this session (e.g. by appUrlOpen) — no need
        // to re-fire from the polled launch URL.
        if (wasAlreadyAppliedThisSession(loc)) {
          return;
        }
        console.log('🔗 Cold-start launch URL → navigate:', loc);
        markNativeDeepLinkHandled(loc);
        markOpenedViaDeepLink();
        navigate(loc, { replace: true });
      };

      if (Capacitor.getPlatform() === 'ios') {
        void getIosLaunchUrlWithRetry().then((url) => {
          if (url) applyLaunchUrl(url);
        });
      } else {
        void App.getLaunchUrl().then((result) => {
          const url = result?.url;
          if (url) applyLaunchUrl(url);
        });
      }

      return () => {
        setDeepLinkNavigator(null);
      };
    }

    const removeListener = deepLinkService.addListener((route: DeepLinkRoute) => {
      console.log('🔗 Deep link navigation triggered:', route);
      if (route.params && Object.keys(route.params).length > 0) {
        const searchParams = new URLSearchParams(route.params);
        navigate(`${route.path}?${searchParams.toString()}`);
      } else {
        navigate(route.path);
      }
    });

    return () => {
      removeListener();
    };
  }, [navigate]);
}
